import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { PickupOptionsPublic } from "@gaole/shared";
import { validatePickupScheduledAt, canChangeOrderShippingAddress } from "@gaole/shared";
import { StorePickupSection } from "../components/pickup/StorePickupSection";
import { PickupScheduleCallout } from "../components/pickup/PickupScheduleCallout";
import { api, ApiError } from "../lib/api";
import { rm, credits } from "../lib/format";
import { useAuth } from "../stores/auth";
import { toast } from "../stores/toast";
import { Button, Input, PageLoader } from "../components/ui";
import { CountrySelect } from "../components/CountrySelect";
import { PhoneInput } from "../components/PhoneInput";
import { AddressRegionField, formatAddressLocation } from "../components/AddressRegionField";
import { shippingLocationQuery } from "../lib/shipping";
import { emptyAddressForm, patchAddressForm, type AddressFormFields } from "../lib/addressForm";
import { PageHeader } from "../components/storefront/PageHeader";
import { PolicyConsentCheckbox } from "../components/PolicyConsentCheckbox";
import { MobileStickyBar, MOBILE_STICKY_BAR_PAD } from "../components/shell/MobileStickyBar";
import { Countdown } from "../components/Countdown";
import { openPaymentGateway } from "../lib/payments";
import { OrderItemThumb } from "../components/storefront/OrderItemThumb";
import { primaryPayButtonLabel, sortPaymentMethods } from "../lib/paymentMethods";
import { ListingTitle } from "../components/ListingTitle";
import { BankInTransferDetails } from "../components/payments/BankInTransferDetails";
import { PaymentMethodPicker } from "../components/payments/PaymentMethodPicker";

interface AddressDto {
  id: string;
  label: string;
  name: string;
  phone: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  postcode: string;
  country: string;
  isDefault: boolean;
}

interface PendingOrderDto {
  id: string;
  orderNo: string;
  kind: string;
  status: string;
  subtotal: number;
  discountTotal: number;
  shippingFee: number;
  total: number;
  shippingAddress: {
    name: string;
    phone: string;
    address1: string;
    address2: string | null;
    postcode: string;
    city: string;
    state: string;
    country: string;
  } | null;
  fulfilmentMethod?: string;
  pickupDetails?: {
    locationId: string;
    name: string;
    address1: string;
    address2?: string | null;
    city: string;
    state: string;
    postcode: string;
    country: string;
    phone?: string | null;
    instructions?: string | null;
    scheduledAt: string;
  } | null;
  paymentDeadline: string | null;
  latestReceiptStatus?: string | null;
  items: {
    id: string;
    name: string;
    price: number;
    discount: number;
    quantity: number;
    imageUrl?: string | null;
    imageBackgroundColor?: string | null;
  }[];
}

interface PendingCheckoutResponse {
  order: PendingOrderDto;
  needsShipping: boolean;
  fulfilmentMethod?: string;
  shippingCountry: string | null;
  shippingState: string | null;
}

type FulfilmentChoice = "DELIVERY" | "PICKUP";

type AddressForm = AddressFormFields;

function emptyAddress(displayName = ""): AddressForm {
  return emptyAddressForm(displayName);
}

const KIND_LABEL: Record<string, string> = {
  RAFFLE_WIN: "Raffle win",
  AUCTION_WIN: "Auction win",
  VAULT_SHIPMENT: "Vault shipment",
  STORE: "Order",
  FLEXXI_DEPOSIT: "FlexxiPlan deposit",
  FLEXXI_PAYMENT: "FlexxiPlan payment",
};

export function CheckoutOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const changingAddress = searchParams.get("changeAddress") === "1";
  const queryClient = useQueryClient();
  const { config, user } = useAuth();
  const [addressId, setAddressId] = useState<string | null>(null);
  const [newAddress, setNewAddress] = useState(() => emptyAddress(user?.displayName ?? ""));
  const [showNewAddress, setShowNewAddress] = useState(false);
  const [method, setMethod] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [acceptPolicies, setAcceptPolicies] = useState(false);
  const [fulfilmentMethod, setFulfilmentMethod] = useState<FulfilmentChoice>("DELIVERY");
  const [pickupLocationId, setPickupLocationId] = useState("");
  const [pickupScheduledAt, setPickupScheduledAt] = useState("");

  const { data: pickupOptions } = useQuery({
    queryKey: ["pickup-options"],
    queryFn: () => api<PickupOptionsPublic>("/shipping/pickup-options"),
  });

  const pickupAvailable = Boolean(pickupOptions?.enabled && pickupOptions.locations.length > 0);

  const { data: addresses, isLoading: addrLoading } = useQuery({
    queryKey: ["addresses"],
    queryFn: () => api<{ items: AddressDto[] }>("/addresses"),
  });

  const shippingLocation = useMemo(() => {
    if (showNewAddress) {
      return {
        country: newAddress.country || undefined,
        state: newAddress.state || undefined,
      };
    }
    const addr = addresses?.items.find((a) => a.id === addressId);
    return addr ? { country: addr.country, state: addr.state } : undefined;
  }, [showNewAddress, newAddress.country, newAddress.state, addressId, addresses?.items]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      "checkout-pending",
      id,
      fulfilmentMethod,
      shippingLocation?.country ?? "",
      shippingLocation?.state ?? "",
    ],
    queryFn: () =>
      api<PendingCheckoutResponse>(
        `/checkout/pending/${id}${shippingLocationQuery({
          ...shippingLocation,
          fulfilmentMethod,
        })}`,
      ),
    enabled: Boolean(id),
  });

  const { data: wallet } = useQuery({
    queryKey: ["wallet"],
    queryFn: () => api<{ balance: number }>("/wallet").catch(() => ({ balance: 0 })),
    enabled: Boolean(config?.creditsEnabled),
  });

  const order = data?.order;
  const needsShipping = data?.needsShipping ?? false;
  const shippingAddressLocked = Boolean(
    order && !canChangeOrderShippingAddress(order.status, order.latestReceiptStatus ?? null),
  );
  const hasAddressOnOrder = Boolean(order?.shippingAddress);
  const hasPickupOnOrder = Boolean(order?.pickupDetails);
  const hasFulfilmentOnOrder = hasAddressOnOrder || hasPickupOnOrder;
  const canQuoteShipping = Boolean(
    shippingLocation?.country && (shippingLocation.country !== "MY" || shippingLocation.state),
  );

  useEffect(() => {
    if (order?.fulfilmentMethod === "PICKUP") setFulfilmentMethod("PICKUP");
    else if (order?.shippingAddress) setFulfilmentMethod("DELIVERY");
  }, [order?.fulfilmentMethod, order?.shippingAddress]);

  useEffect(() => {
    if (!pickupAvailable && fulfilmentMethod === "PICKUP") {
      setFulfilmentMethod("DELIVERY");
    }
  }, [pickupAvailable, fulfilmentMethod]);

  useEffect(() => {
    if (!order || !changingAddress || !shippingAddressLocked) return;
    toast("info", "Shipping address is locked while payment is verified or after payment.");
    navigate(`/orders/${order.id}`, { replace: true });
  }, [order, changingAddress, shippingAddressLocked, navigate]);

  useEffect(() => {
    if (pickupAvailable && pickupOptions?.locations.length && !pickupLocationId) {
      setPickupLocationId(pickupOptions.locations[0].id);
    }
  }, [pickupAvailable, pickupOptions?.locations, pickupLocationId]);

  const methods = useMemo(() => {
    const list: string[] = [];
    if (config?.creditsEnabled && order?.kind !== "CREDIT_TOPUP") list.push("CREDITS");
    if (config?.paymentMethods.stripe) list.push("STRIPE");
    if (config?.paymentMethods.gkash) list.push("GKASH");
    if (config?.paymentMethods.bankIn) list.push("BANK_IN");
    return sortPaymentMethods(list);
  }, [config, order?.kind]);

  useEffect(() => {
    if (!needsShipping || hasFulfilmentOnOrder) return;
    if (showNewAddress) return;
    if (addresses?.items.length && !addressId) {
      const def = addresses.items.find((a) => a.isDefault) ?? addresses.items[0];
      setAddressId(def.id);
      setShowNewAddress(false);
    }
    if (addresses && addresses.items.length === 0) {
      setShowNewAddress(true);
      setAddressId(null);
    }
  }, [addresses, addressId, needsShipping, hasFulfilmentOnOrder, showNewAddress]);

  useEffect(() => {
    if (!method && methods.length) setMethod(methods[0]);
  }, [methods, method]);

  const refreshCheckout = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (isLoading || addrLoading) return <PageLoader />;
  if (!order) {
    navigate("/orders");
    return null;
  }
  const checkoutOrder = order;
  const total = checkoutOrder.total;
  const creditCost = Math.ceil(total * (config?.creditConversionRate ?? 1));
  const mustCollectFulfilment =
    needsShipping && (!hasFulfilmentOnOrder || (changingAddress && !shippingAddressLocked));
  const requiresPolicies = needsShipping && !hasFulfilmentOnOrder;
  const selectedPickupLocation = pickupOptions?.locations.find((l) => l.id === pickupLocationId);

  function validateNewAddress(): string | null {
    if (!newAddress.label.trim()) return "Add a label (e.g. Home)";
    if (!newAddress.name.trim()) return "Recipient name is required";
    if (!newAddress.phone.trim()) return "Phone is required";
    if (!newAddress.address1.trim()) return "Address line 1 is required";
    if (!newAddress.postcode.trim()) return "Postcode is required";
    if (!newAddress.city.trim()) return "City is required";
    if (!newAddress.country.trim()) return "Country is required";
    if (!newAddress.state.trim()) return newAddress.country === "MY" ? "State is required" : "State or region is required";
    return null;
  }

  async function submit() {
    setSubmitting(true);
    try {
      let finalAddressId = addressId ?? undefined;
      if (mustCollectFulfilment && fulfilmentMethod === "DELIVERY" && showNewAddress) {
        const err = validateNewAddress();
        if (err) {
          toast("error", err);
          setSubmitting(false);
          return;
        }
        const created = await api<{ address: AddressDto }>("/addresses", {
          body: {
            ...newAddress,
            address2: newAddress.address2 || null,
          },
        });
        finalAddressId = created.address.id;
        await queryClient.invalidateQueries({ queryKey: ["addresses"] });
        toast("success", "Address saved for next time");
      }
      if (mustCollectFulfilment && fulfilmentMethod === "PICKUP") {
        if (!pickupLocationId || !pickupScheduledAt) {
          toast("error", "Select a pickup location and time");
          setSubmitting(false);
          return;
        }
        if (pickupOptions) {
          const pickupErr = validatePickupScheduledAt(
            new Date(pickupScheduledAt),
            new Date(),
            pickupOptions.minLeadHours,
            pickupOptions.maxDaysAhead,
            selectedPickupLocation?.availability,
          );
          if (pickupErr) {
            toast("error", pickupErr);
            setSubmitting(false);
            return;
          }
        }
        if (!acceptPolicies && requiresPolicies) {
          toast("error", "Accept the store policies to continue");
          setSubmitting(false);
          return;
        }
        await api(`/checkout/pending/${checkoutOrder.id}/pickup`, {
          body: {
            pickupLocationId,
            pickupScheduledAt,
          },
        });
        await refetch();
      } else if (mustCollectFulfilment && fulfilmentMethod === "DELIVERY" && !finalAddressId) {
        toast("error", "Pick or create a shipping address");
        setSubmitting(false);
        return;
      }
      if (mustCollectFulfilment && fulfilmentMethod === "DELIVERY" && !acceptPolicies && requiresPolicies) {
        toast("error", "Accept the store policies to continue");
        setSubmitting(false);
        return;
      }

      const res = await api<{ status: string; redirectUrl?: string; form?: { url: string; fields: Record<string, string> } }>(
        `/checkout/pay/${checkoutOrder.id}`,
        {
          body: {
            method,
            ...(mustCollectFulfilment && fulfilmentMethod === "DELIVERY" && finalAddressId
              ? {
                  addressId: finalAddressId,
                  fulfilmentMethod: "DELIVERY" as const,
                  ...(requiresPolicies ? { acceptPolicies: true as const } : {}),
                }
              : {}),
            ...(mustCollectFulfilment && fulfilmentMethod === "PICKUP"
              ? {
                  fulfilmentMethod: "PICKUP" as const,
                  pickupLocationId,
                  pickupScheduledAt,
                  ...(requiresPolicies ? { acceptPolicies: true as const } : {}),
                }
              : {}),
          },
        },
      );

      if (openPaymentGateway(res)) return;

      if (res.status === "PAID") {
        toast("success", "Paid!");
        await queryClient.invalidateQueries({ queryKey: ["wallet"] });
        navigate(`/orders/${checkoutOrder.id}`);
        return;
      }

      if (res.status === "AWAITING_RECEIPT") {
        navigate(`/orders/${checkoutOrder.id}#bank-in-payment`, { replace: true });
        return;
      }

      if (res.status === "REDIRECT" || res.status === "GKASH_FORM") {
        toast("error", "Couldn't open the payment gateway — try again from your order.");
      }
      navigate(`/orders/${checkoutOrder.id}`);
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Payment failed");
    } finally {
      setSubmitting(false);
    }
  }

  const methodLabel: Record<string, string> = {
    STRIPE: "Card (Stripe)",
    GKASH: "Gkash · FPX / eWallet",
    BANK_IN: "Manual bank transfer",
    CREDITS: `Credits${wallet ? ` · balance ${credits(wallet.balance)}` : ""}`,
  };

  const title = KIND_LABEL[order.kind] ?? "Complete payment";

  const amountLabel = method === "CREDITS" ? credits(creditCost) : rm(total);
  const submitLabel = submitting
    ? method === "BANK_IN"
      ? "Creating order…"
      : "Processing…"
    : primaryPayButtonLabel(method, amountLabel);

  return (
    <div className={`space-y-5 px-4 pt-4 ${MOBILE_STICKY_BAR_PAD} md:pb-8`}>
      <PageHeader
        title={title}
        description={
          changingAddress
            ? `Order ${order.orderNo} — choose a new shipping address.`
            : `Order ${order.orderNo} — add shipping and pay to claim your prize.`
        }
      />

      {order.paymentDeadline && (
        <div className="rounded-2xl border border-amber-400/30 bg-amber-500/10 p-3.5 text-center">
          <p className="text-[11px] text-amber-300">Pay before the deadline or your win is forfeited</p>
          <div className="mt-1 flex justify-center">
            <Countdown target={order.paymentDeadline} onDone={refreshCheckout} />
          </div>
        </div>
      )}

      {changingAddress && (
        <section className="space-y-2">
          <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">
            Shipping address
          </h2>
          <p className="text-[11px] text-white/45">Choose a new delivery address for this order.</p>
          {addresses?.items.map((addr) => (
            <button
              key={addr.id}
              type="button"
              onClick={() => {
                setAddressId(addr.id);
                setShowNewAddress(false);
              }}
              className={`w-full rounded-2xl border p-3 text-left text-xs transition-colors ${
                addressId === addr.id && !showNewAddress
                  ? "border-brand-pink bg-brand-pink/10"
                  : "border-white/5 bg-surface"
              }`}
            >
              <p className="font-semibold">{addr.label}</p>
              <p className="mt-1 font-medium text-white/90">
                {addr.name} <span className="font-normal text-white/50">· {addr.phone}</span>
              </p>
              <p className="mt-0.5 text-white/60">
                {addr.address1}
                {addr.address2 ? `, ${addr.address2}` : ""},{" "}
                {formatAddressLocation(addr)}
              </p>
            </button>
          ))}
        </section>
      )}

      {mustCollectFulfilment && !changingAddress && (
        <section className="space-y-3">
          {pickupAvailable && (
            <div className="flex gap-1.5 rounded-full bg-surface p-1">
              <button
                type="button"
                onClick={() => setFulfilmentMethod("DELIVERY")}
                className={`flex-1 rounded-full py-1.5 text-xs font-semibold transition-colors ${
                  fulfilmentMethod === "DELIVERY" ? "bg-gradient-brand text-white" : "text-white/50"
                }`}
              >
                Delivery
              </button>
              <button
                type="button"
                onClick={() => setFulfilmentMethod("PICKUP")}
                className={`flex-1 rounded-full py-1.5 text-xs font-semibold transition-colors ${
                  fulfilmentMethod === "PICKUP" ? "bg-gradient-brand text-white" : "text-white/50"
                }`}
              >
                Pickup
              </button>
            </div>
          )}

          {fulfilmentMethod === "PICKUP" && pickupOptions ? (
            <StorePickupSection
              pickupOptions={pickupOptions}
              locationId={pickupLocationId}
              onLocationIdChange={setPickupLocationId}
              scheduledAt={pickupScheduledAt}
              onScheduledAtChange={setPickupScheduledAt}
            />
          ) : (
            <>
          <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">
            Shipping address
          </h2>
          <p className="text-[11px] text-white/45">
            Select a saved address or create one — delivery fee is calculated from your country and state.
          </p>

          {addresses?.items.map((addr) => (
            <button
              key={addr.id}
              type="button"
              onClick={() => {
                setAddressId(addr.id);
                setShowNewAddress(false);
              }}
              className={`w-full rounded-2xl border p-3 text-left text-xs transition-colors ${
                addressId === addr.id && !showNewAddress
                  ? "border-brand-pink bg-brand-pink/10"
                  : "border-white/5 bg-surface"
              }`}
            >
              <p className="font-semibold">
                {addr.label}
                {addr.isDefault && (
                  <span className="ml-1.5 rounded-full bg-brand-pink/20 px-1.5 py-0.5 text-[9px] font-medium text-brand-pink">
                    Default
                  </span>
                )}
              </p>
              <p className="mt-1 font-medium text-white/90">
                {addr.name} <span className="font-normal text-white/50">· {addr.phone}</span>
              </p>
              <p className="mt-0.5 text-white/60">
                {addr.address1}
                {addr.address2 ? `, ${addr.address2}` : ""},{" "}
                {formatAddressLocation(addr)}
              </p>
            </button>
          ))}

          {!showNewAddress ? (
            <button
              type="button"
              onClick={() => {
                setShowNewAddress(true);
                setAddressId(null);
                setNewAddress(emptyAddress(user?.displayName ?? ""));
              }}
              className="text-xs font-medium text-brand-pink"
            >
              + Add new address
            </button>
          ) : (
            <div className="space-y-2 rounded-2xl border border-brand-pink/30 bg-surface p-3">
              <p className="font-display text-[10px] font-semibold uppercase tracking-widest text-white/40">
                New address
              </p>
              <Input
                label="Label"
                placeholder="Home, Office…"
                value={newAddress.label}
                onChange={(e) => patchAddressForm(setNewAddress, { label: e.target.value })}
              />
              <Input
                label="Recipient name"
                placeholder="Full name"
                value={newAddress.name}
                onChange={(e) => patchAddressForm(setNewAddress, { name: e.target.value })}
              />
              <PhoneInput
                label="Phone"
                placeholder="Mobile number"
                value={newAddress.phone}
                onChange={(phone) => patchAddressForm(setNewAddress, { phone })}
                country={newAddress.country}
              />
              <Input
                label="Address line 1"
                placeholder="Street address"
                value={newAddress.address1}
                onChange={(e) => patchAddressForm(setNewAddress, { address1: e.target.value })}
              />
              <Input
                label="Address line 2"
                placeholder="Unit, floor (optional)"
                value={newAddress.address2}
                onChange={(e) => patchAddressForm(setNewAddress, { address2: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  label="Postcode"
                  placeholder="Postcode"
                  inputMode="numeric"
                  value={newAddress.postcode}
                  onChange={(e) => patchAddressForm(setNewAddress, { postcode: e.target.value })}
                />
                <Input
                  label="City"
                  placeholder="City"
                  value={newAddress.city}
                  onChange={(e) => patchAddressForm(setNewAddress, { city: e.target.value })}
                />
              </div>
              <CountrySelect
                label="Country"
                required
                value={newAddress.country}
                onChange={(country) =>
                  patchAddressForm(setNewAddress, (prev) => ({
                    country,
                    state: country === prev.country ? prev.state : "",
                  }))
                }
              />
              <AddressRegionField
                country={newAddress.country}
                state={newAddress.state}
                onStateChange={(state) => patchAddressForm(setNewAddress, { state })}
              />
              <label className="flex items-center gap-2 pt-1 text-xs text-white/70">
                <input
                  type="checkbox"
                  checked={newAddress.isDefault}
                  onChange={(e) => patchAddressForm(setNewAddress, { isDefault: e.target.checked })}
                  className="rounded border-white/20 bg-transparent"
                />
                Set as default for next checkout
              </label>
              {addresses && addresses.items.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setShowNewAddress(false);
                    const def = addresses.items.find((a) => a.isDefault) ?? addresses.items[0];
                    setAddressId(def.id);
                  }}
                  className="text-xs text-white/50"
                >
                  Use saved address instead
                </button>
              )}
            </div>
          )}
            </>
          )}
        </section>
      )}

      {hasPickupOnOrder && order.pickupDetails && (
        <PickupScheduleCallout details={order.pickupDetails} />
      )}

      {hasAddressOnOrder && order.shippingAddress && (
        <section className="rounded-2xl border border-white/5 bg-surface p-4 text-xs">
          <p className="mb-1 font-display text-[10px] font-semibold uppercase tracking-widest text-white/40">
            Ship to
          </p>
          <p className="font-semibold">
            {order.shippingAddress.name}{" "}
            <span className="font-normal text-white/50">· {order.shippingAddress.phone}</span>
          </p>
          <p className="mt-0.5 text-white/60">
            {order.shippingAddress.address1}
            {order.shippingAddress.address2 ? `, ${order.shippingAddress.address2}` : ""},{" "}
            {order.shippingAddress.postcode} {order.shippingAddress.city}, {order.shippingAddress.state}
          </p>
        </section>
      )}

      <PaymentMethodPicker
        methods={methods}
        value={method}
        onChange={setMethod}
        labelForMethod={(m) => methodLabel[m] ?? m}
        isMethodDisabled={(m) => m === "CREDITS" && (wallet?.balance ?? 0) < creditCost}
        methodDetails={(m) => {
          if (m === "BANK_IN") {
            return (
              <BankInTransferDetails
                instructions={config?.bankInInstructions}
                bank={config?.bankInBank}
                accountNumber={config?.bankInAccountNumber}
                accountName={config?.bankInAccountName}
                qrImageUrl={config?.bankInQrImageUrl}
                instructionsFallback="You'll confirm next, then upload your transfer receipt on the pending payment page."
              />
            );
          }
          if (m === "STRIPE") {
            return (
              <p className="rounded-xl border border-white/5 bg-surface-2/50 px-3 py-2.5 text-[11px] text-white/50">
                Redirect to Stripe after you continue.
              </p>
            );
          }
          if (m === "GKASH") {
            return (
              <p className="rounded-xl border border-white/5 bg-surface-2/50 px-3 py-2.5 text-[11px] text-white/50">
                Continue on Gkash after you confirm.
              </p>
            );
          }
          if (m === "CREDITS") {
            return (
              <p className="rounded-xl border border-white/5 bg-surface-2/50 px-3 py-2.5 text-[11px] text-white/50">
                Debits your wallet when you pay.
              </p>
            );
          }
          return null;
        }}
      />

      <section className="rounded-2xl border border-white/5 bg-surface p-4 text-xs">
        {order.items.map((item) => (
          <div key={item.id} className="flex items-center gap-2.5 py-1.5">
            <OrderItemThumb
              src={item.imageUrl}
              alt={item.name}
              backgroundColor={item.imageBackgroundColor}
              className="h-10 w-10 shrink-0 rounded-lg"
            />
            <ListingTitle as="span" className="min-w-0 flex-1 text-white/70">
              {`${item.name} ×${item.quantity}`}
            </ListingTitle>
          </div>
        ))}
        <div className="mt-2 space-y-1 border-t border-white/10 pt-2">
          <div className="flex justify-between text-white/60">
            <span>Subtotal</span>
            <span>{rm(order.subtotal)}</span>
          </div>
          {order.discountTotal > 0 && (
            <div className="flex justify-between text-emerald-400">
              <span>Discounts</span>
              <span>−{rm(order.discountTotal)}</span>
            </div>
          )}
          <div className="flex justify-between text-white/60">
            <span>Shipping</span>
            <span>
              {fulfilmentMethod === "PICKUP" || hasPickupOnOrder
                ? "FREE (Pickup)"
                : order.shippingFee === 0
                  ? needsShipping && !canQuoteShipping && !hasAddressOnOrder
                    ? "—"
                    : "FREE"
                  : rm(order.shippingFee)}
            </span>
          </div>
          {needsShipping &&
            fulfilmentMethod === "DELIVERY" &&
            !canQuoteShipping &&
            !hasAddressOnOrder &&
            !hasPickupOnOrder && (
            <p className="text-[10px] text-white/40">Select a country and state to see the delivery fee for your address.</p>
          )}
          <div className="flex justify-between pt-1 font-display text-sm font-bold">
            <span>Total</span>
            <span className="text-brand-pink">
              {method === "CREDITS" ? credits(creditCost) : rm(total)}
            </span>
          </div>
        </div>
      </section>

      {mustCollectFulfilment && requiresPolicies && (
        <PolicyConsentCheckbox variant="checkout" checked={acceptPolicies} onChange={setAcceptPolicies} />
      )}

      <p className="text-center text-[11px] text-white/40">
        <Link to={`/orders/${order.id}`} className="text-brand-pink">
          View order details
        </Link>
      </p>

      <MobileStickyBar active>
        <Button
          className="w-full"
          disabled={
            submitting ||
            !method ||
            (requiresPolicies && !acceptPolicies) ||
            (mustCollectFulfilment && fulfilmentMethod === "DELIVERY" && !canQuoteShipping)
          }
          onClick={submit}
        >
          {submitLabel}
        </Button>
      </MobileStickyBar>

      <div className="hidden md:block">
        <Button
          className="w-full"
          disabled={
            submitting ||
            !method ||
            (requiresPolicies && !acceptPolicies) ||
            (mustCollectFulfilment && fulfilmentMethod === "DELIVERY" && !canQuoteShipping)
          }
          onClick={submit}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
